import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import {
  BarChart3, DollarSign, Users, TrendingUp, Heart, MessageCircle,
  Share2, Music, Swords, Eye, ArrowLeft, Loader2, UserPlus,
  ArrowUpRight, ArrowDownRight, Play, Clock, Trophy, Zap
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const CHART_COLORS = {
  primary: '#007AFF',
  secondary: '#FF3B30',
  success: '#34D399',
  warning: '#FBBF24',
  grid: 'rgba(255,255,255,0.05)',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' && p.name?.toLowerCase().includes('earn') ? `$${p.value.toFixed(2)}` : p.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

// KPI Card
function KPICard({ label, value, icon: Icon, color, trend, trendLabel, testId }) {
  const colorMap = {
    blue: { border: 'border-[#007AFF]/30', icon: 'text-[#007AFF]', bg: 'bg-[#007AFF]/10' },
    green: { border: 'border-[#34D399]/30', icon: 'text-[#34D399]', bg: 'bg-[#34D399]/10' },
    red: { border: 'border-[#FF3B30]/30', icon: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/10' },
    purple: { border: 'border-[#A78BFA]/30', icon: 'text-[#A78BFA]', bg: 'bg-[#A78BFA]/10' },
    amber: { border: 'border-[#FBBF24]/30', icon: 'text-[#FBBF24]', bg: 'bg-[#FBBF24]/10' },
    white: { border: 'border-white/20', icon: 'text-white', bg: 'bg-white/10' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <Card
      data-testid={testId}
      className={`bg-[#141414] ${c.border} border rounded-xl p-5 flex flex-col gap-2 min-w-[160px] snap-start shrink-0`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">{label}</span>
        <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
      </div>
      <p className="text-3xl sm:text-4xl font-bold tracking-tighter" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {(trend !== undefined || trendLabel) && (
        <div className="flex items-center gap-1.5 text-xs">
          {trend > 0 && <ArrowUpRight className="w-3.5 h-3.5 text-[#34D399]" />}
          {trend < 0 && <ArrowDownRight className="w-3.5 h-3.5 text-[#FF3B30]" />}
          <span className={trend > 0 ? 'text-[#34D399]' : trend < 0 ? 'text-[#FF3B30]' : 'text-zinc-400'}>
            {trend > 0 ? '+' : ''}{trend}{trendLabel ? ` ${trendLabel}` : ''}
          </span>
        </div>
      )}
    </Card>
  );
}

// Score badge
function ScoreBadge({ label }) {
  const styles = {
    trending: 'bg-[#34D399]/15 text-[#34D399] border-[#34D399]/30',
    high_engagement: 'bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/30',
    growing: 'bg-[#FBBF24]/15 text-[#FBBF24] border-[#FBBF24]/30',
    needs_improvement: 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30',
  };
  const labels = {
    trending: 'Trending',
    high_engagement: 'High Engagement',
    growing: 'Growing',
    needs_improvement: 'Needs Improvement',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold border ${styles[label] || styles.growing}`}>
      {labels[label] || label}
    </span>
  );
}

// Heatmap component
function ActivityHeatmap({ data }) {
  if (!data?.length) return <p className="text-zinc-500 text-sm text-center py-8">No activity data yet</p>;
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const maxVal = Math.max(...data.map(d => d.value), 1);

  return (
    <div className="overflow-x-auto" data-testid="best-time-heatmap">
      <div className="min-w-[600px]">
        <div className="flex gap-0.5 mb-1 pl-10">
          {Array.from({ length: 24 }, (_, i) => (
            <div key={i} className="flex-1 text-center text-[9px] text-zinc-500">
              {i % 3 === 0 ? `${i}h` : ''}
            </div>
          ))}
        </div>
        {days.map(day => (
          <div key={day} className="flex gap-0.5 items-center mb-0.5">
            <span className="w-9 text-[10px] text-zinc-500 text-right pr-1">{day}</span>
            {Array.from({ length: 24 }, (_, hour) => {
              const point = data.find(d => d.day === day && d.hour === hour);
              const val = point?.value || 0;
              const opacity = val > 0 ? Math.max(0.15, val / maxVal) : 0.03;
              return (
                <div
                  key={hour}
                  className="flex-1 aspect-square rounded-sm"
                  style={{ backgroundColor: `rgba(0, 122, 255, ${opacity})` }}
                  title={`${day} ${hour}:00 - ${val} engagement`}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');

  // Data states
  const [overview, setOverview] = useState(null);
  const [tracks, setTracks] = useState(null);
  const [battles, setBattles] = useState(null);
  const [audience, setAudience] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);

  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      if (tab === 'overview' || !tab) {
        const [ovRes, feedRes] = await Promise.all([
          fetch(`${API}/api/analytics/advanced/overview?time_range=${timeRange}`, { headers }),
          fetch(`${API}/api/analytics/advanced/activity-feed?limit=15`, { headers }),
        ]);
        if (ovRes.ok) setOverview(await ovRes.json());
        if (feedRes.ok) { const d = await feedRes.json(); setActivityFeed(d.activity || []); }
      } else if (tab === 'tracks') {
        const res = await fetch(`${API}/api/analytics/advanced/tracks?time_range=${timeRange}`, { headers });
        if (res.ok) setTracks(await res.json());
      } else if (tab === 'battles') {
        const res = await fetch(`${API}/api/analytics/advanced/battles`, { headers });
        if (res.ok) setBattles(await res.json());
      } else if (tab === 'audience') {
        const res = await fetch(`${API}/api/analytics/advanced/audience?time_range=${timeRange}`, { headers });
        if (res.ok) setAudience(await res.json());
      } else if (tab === 'earnings') {
        const [eRes, engRes] = await Promise.all([
          fetch(`${API}/api/analytics/advanced/earnings?time_range=${timeRange}`, { headers }),
          fetch(`${API}/api/analytics/advanced/engagement?time_range=${timeRange}`, { headers }),
        ]);
        if (eRes.ok) setEarnings(await eRes.json());
        if (engRes.ok) setEngagement(await engRes.json());
      }
    } catch (err) {
      console.error('[Analytics] fetch error:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, timeRange]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData(activeTab);
  }, [isAuthenticated, activeTab, timeRange, fetchData, navigate]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const fmt = (n) => typeof n === 'number' ? n.toLocaleString() : '0';
  const fmtUSD = (n) => `$${(n || 0).toFixed(2)}`;

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#007AFF] mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const m = overview?.metrics || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="analytics-page">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid="analytics-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Analytics</h1>
                <p className="text-xs text-zinc-500">Actionable insights for your content</p>
              </div>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[130px] bg-[#141414] border-white/10 text-sm" data-testid="time-filter-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1E] border-white/10">
                <SelectItem value="today" data-testid="time-filter-option-today">Today</SelectItem>
                <SelectItem value="week" data-testid="time-filter-option-7d">7 Days</SelectItem>
                <SelectItem value="month" data-testid="time-filter-option-30d">30 Days</SelectItem>
                <SelectItem value="all" data-testid="time-filter-option-all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-[#141414] border border-white/10 p-1 mb-6 w-full justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="overview" data-testid="overview-tab" className="data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
            <TabsTrigger value="tracks" data-testid="tracks-tab" className="data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm whitespace-nowrap">Tracks / Beats</TabsTrigger>
            <TabsTrigger value="battles" data-testid="battles-tab" className="data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm whitespace-nowrap">Battles</TabsTrigger>
            <TabsTrigger value="audience" data-testid="audience-tab" className="data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm whitespace-nowrap">Audience</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="earnings-tab" className="data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm whitespace-nowrap">Earnings</TabsTrigger>
          </TabsList>

          {/* ==================== OVERVIEW TAB ==================== */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPI Cards - swipeable on mobile */}
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 scrollbar-hide">
              <KPICard label="Total Plays" value={fmt(m.total_plays)} icon={Play} color="blue" testId="kpi-card-plays" />
              <KPICard label="Followers" value={fmt(m.total_followers)} icon={Users} color="purple" trend={m.follower_trend} trendLabel="this period" testId="kpi-card-followers" />
              <KPICard label="Reactions" value={fmt(m.total_reactions)} icon={Heart} color="red" testId="kpi-card-reactions" />
              <KPICard label="Comments" value={fmt(m.total_comments)} icon={MessageCircle} color="amber" testId="kpi-card-comments" />
              <KPICard label="Earnings" value={fmtUSD(m.total_earnings)} icon={DollarSign} color="green" testId="kpi-card-earnings" />
              <KPICard label="Streams" value={fmt(m.total_streams)} icon={Eye} color="white" testId="kpi-card-streams" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Follower Growth Chart */}
              <Card className="bg-[#141414] border-white/10 rounded-xl p-5" data-testid="growth-line-chart">
                <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Follower Growth</h3>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview?.growth_chart || []}>
                      <defs>
                        <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#007AFF" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                      <XAxis dataKey="date" stroke="#71717A" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="followers" stroke="#007AFF" fill="url(#growthGrad)" strokeWidth={2} name="Followers" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Earnings Chart */}
              <Card className="bg-[#141414] border-white/10 rounded-xl p-5" data-testid="earnings-bar-chart">
                <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Earnings Trend</h3>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={overview?.earnings_chart || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                      <XAxis dataKey="date" stroke="#71717A" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="earnings" fill="#34D399" radius={[4, 4, 0, 0]} name="Earnings" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            {/* Activity Feed */}
            <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden" data-testid="activity-feed">
              <div className="px-5 py-4 border-b border-white/5">
                <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Recent Activity</h3>
              </div>
              {activityFeed.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Zap className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
                  {activityFeed.map((item, idx) => (
                    <div key={idx} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        item.type === 'follow' ? 'bg-[#A78BFA]/15' :
                        item.type === 'reaction' ? 'bg-[#FF3B30]/15' :
                        item.type === 'earning' ? 'bg-[#34D399]/15' :
                        'bg-[#007AFF]/15'
                      }`}>
                        {item.type === 'follow' && <UserPlus className="w-3.5 h-3.5 text-[#A78BFA]" />}
                        {item.type === 'reaction' && <Heart className="w-3.5 h-3.5 text-[#FF3B30]" />}
                        {item.type === 'earning' && <DollarSign className="w-3.5 h-3.5 text-[#34D399]" />}
                        {item.type === 'comment' && <MessageCircle className="w-3.5 h-3.5 text-[#007AFF]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-200 truncate">{item.message}</p>
                        <p className="text-[11px] text-zinc-500">{timeAgo(item.created_at)}</p>
                      </div>
                      {item.amount_usd > 0 && (
                        <span className="text-sm font-semibold text-[#34D399]">+{fmtUSD(item.amount_usd)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ==================== TRACKS TAB ==================== */}
          <TabsContent value="tracks" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" /></div>
            ) : (
              <>
                {/* Track Summary */}
                {tracks?.summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Plays</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(tracks.summary.total_plays)}</p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Likes</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(tracks.summary.total_likes)}</p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Avg Score</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tracks.summary.avg_score}/100</p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Top Track</p>
                      <p className="text-sm font-semibold mt-1 truncate">{tracks.summary.top_track || '-'}</p>
                    </Card>
                  </div>
                )}

                {/* Track Table */}
                <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5">
                    <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Track Performance</h3>
                  </div>
                  {!tracks?.tracks?.length ? (
                    <div className="p-8 text-center text-zinc-500">
                      <Music className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No tracks yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="tracks-table">
                        <thead>
                          <tr className="border-b border-white/5">
                            <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Track</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Plays</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold hidden sm:table-cell">Likes</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold hidden md:table-cell">Comments</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold hidden md:table-cell">Shares</th>
                            <th className="text-center px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Score</th>
                            <th className="text-center px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tracks.tracks.map((t) => (
                            <tr key={t.track_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
                                    {t.cover_art ? <img src={t.cover_art} alt="" className="w-full h-full object-cover" /> : <Music className="w-4 h-4 text-zinc-500" />}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium truncate max-w-[180px]">{t.title}</p>
                                    <p className="text-[10px] text-zinc-500">{t.genre || 'Unknown genre'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-right px-3 py-3 font-mono text-zinc-300">{fmt(t.plays)}</td>
                              <td className="text-right px-3 py-3 font-mono text-zinc-300 hidden sm:table-cell">{fmt(t.likes)}</td>
                              <td className="text-right px-3 py-3 font-mono text-zinc-300 hidden md:table-cell">{fmt(t.comments)}</td>
                              <td className="text-right px-3 py-3 font-mono text-zinc-300 hidden md:table-cell">{fmt(t.shares)}</td>
                              <td className="text-center px-3 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-12 h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${t.score}%`, backgroundColor: t.score >= 75 ? '#34D399' : t.score >= 45 ? '#007AFF' : t.score >= 15 ? '#FBBF24' : '#FF3B30' }} />
                                  </div>
                                  <span className="text-xs font-mono text-zinc-400">{t.score}</span>
                                </div>
                              </td>
                              <td className="text-center px-3 py-3"><ScoreBadge label={t.score_label} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== BATTLES TAB ==================== */}
          <TabsContent value="battles" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" /></div>
            ) : (
              <>
                {battles?.summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Battles</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{battles.summary.total_entered}</p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Win Rate</p>
                      <p className="text-2xl font-bold mt-1 text-[#34D399]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{battles.summary.win_rate}%</p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">W / L</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <span className="text-[#34D399]">{battles.summary.wins}</span>
                        <span className="text-zinc-500"> / </span>
                        <span className="text-[#FF3B30]">{battles.summary.losses}</span>
                      </p>
                    </Card>
                    <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Reactions</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(battles.summary.total_reactions)}</p>
                    </Card>
                  </div>
                )}

                {battles?.summary?.top_performance_battle && (
                  <Card className="bg-[#141414] border-[#FBBF24]/30 rounded-xl p-5">
                    <div className="flex items-center gap-3">
                      <Trophy className="w-6 h-6 text-[#FBBF24]" />
                      <div>
                        <p className="text-xs text-zinc-400 uppercase tracking-wider">Top Performance Battle</p>
                        <p className="text-lg font-bold">{battles.summary.top_performance_battle}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Battle List */}
                <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5">
                    <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Battle History</h3>
                  </div>
                  {!battles?.battles?.length ? (
                    <div className="p-8 text-center text-zinc-500">
                      <Swords className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No battles yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {battles.battles.map((b) => (
                        <div key={b.battle_id} className="px-5 py-3 flex items-center justify-between hover:bg-white/[0.02]">
                          <div>
                            <p className="font-medium text-sm">{b.title}</p>
                            <p className="text-[11px] text-zinc-500">{b.status}</p>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-zinc-400">{b.reactions} reactions</span>
                            <Badge className={`${
                              b.result === 'win' ? 'bg-[#34D399]/15 text-[#34D399] border-[#34D399]/30' :
                              b.result === 'loss' ? 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30' :
                              'bg-white/10 text-zinc-400 border-white/10'
                            } text-[10px] border`}>
                              {b.result === 'win' ? 'WIN' : b.result === 'loss' ? 'LOSS' : 'PENDING'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== AUDIENCE TAB ==================== */}
          <TabsContent value="audience" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" /></div>
            ) : (
              <>
                {/* Audience KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Followers</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(audience?.total_followers)}</p>
                  </Card>
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">New Followers</p>
                    <p className="text-2xl font-bold mt-1 text-[#34D399]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>+{fmt(audience?.new_followers)}</p>
                  </Card>
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">New Listeners</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(audience?.new_vs_returning?.new_listeners)}</p>
                  </Card>
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-4">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Returning</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(audience?.new_vs_returning?.returning_listeners)}</p>
                  </Card>
                </div>

                {/* Growth Chart */}
                <Card className="bg-[#141414] border-white/10 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Follower Growth</h3>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={audience?.growth_chart || []}>
                        <defs>
                          <linearGradient id="audGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#A78BFA" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#A78BFA" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                        <XAxis dataKey="date" stroke="#71717A" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="followers" stroke="#A78BFA" fill="url(#audGrad)" strokeWidth={2} name="Followers" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                {/* Top Supporters */}
                <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5">
                    <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Top Supporters</h3>
                  </div>
                  {!audience?.top_supporters?.length ? (
                    <div className="p-8 text-center text-zinc-500">
                      <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No supporters yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {audience.top_supporters.map((s) => (
                        <div key={s.user_id} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02]">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            s.rank === 1 ? 'bg-[#FBBF24] text-black' :
                            s.rank === 2 ? 'bg-zinc-400 text-black' :
                            s.rank === 3 ? 'bg-amber-700 text-white' :
                            'bg-white/10 text-zinc-400'
                          }`}>{s.rank}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{s.name}</p>
                            <p className="text-[11px] text-zinc-500">{s.total_gifts} gifts</p>
                          </div>
                          <span className="text-sm font-semibold text-[#34D399]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtUSD(s.total_value_usd)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== EARNINGS TAB ==================== */}
          <TabsContent value="earnings" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#007AFF]" /></div>
            ) : (
              <>
                {/* Earnings KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <Card className="bg-[#141414] border-[#34D399]/30 rounded-xl p-5">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Earnings</p>
                    <p className="text-3xl font-bold mt-1 text-[#34D399]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtUSD(earnings?.total_earnings)}</p>
                  </Card>
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-5">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Battle Earnings</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtUSD(earnings?.battle_earnings?.total)}</p>
                  </Card>
                  <Card className="bg-[#141414] border-white/10 rounded-xl p-5 col-span-2 sm:col-span-1">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Sources</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{earnings?.by_source?.length || 0}</p>
                  </Card>
                </div>

                {/* Earnings Chart */}
                <Card className="bg-[#141414] border-white/10 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Earnings Trend</h3>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={earnings?.earnings_chart || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />
                        <XAxis dataKey="date" stroke="#71717A" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="tips" stackId="a" fill="#007AFF" radius={[0, 0, 0, 0]} name="Tips" />
                        <Bar dataKey="gifts" stackId="a" fill="#34D399" radius={[4, 4, 0, 0]} name="Gifts" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                {/* Earnings by Source */}
                {earnings?.by_source?.length > 0 && (
                  <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/5">
                      <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">By Source</h3>
                    </div>
                    <div className="divide-y divide-white/5">
                      {earnings.by_source.map((s) => (
                        <div key={s.source} className="px-5 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#007AFF]" />
                            <span className="text-sm capitalize">{s.source}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-24 h-2 rounded-full bg-white/10 overflow-hidden">
                              <div className="h-full rounded-full bg-[#34D399]" style={{ width: `${s.percentage}%` }} />
                            </div>
                            <span className="text-sm font-mono text-[#34D399] w-16 text-right">{fmtUSD(s.total)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Engagement Insights */}
                {engagement && (
                  <>
                    {/* Best Posting Hours */}
                    {engagement.best_posting_hours?.length > 0 && (
                      <Card className="bg-[#141414] border-white/10 rounded-xl p-5">
                        <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Best Posting Times</h3>
                        <div className="space-y-3">
                          {engagement.best_posting_hours.map((h) => (
                            <div key={h.hour} className="flex items-center gap-3">
                              <Clock className="w-4 h-4 text-zinc-500" />
                              <span className="text-sm w-16">{h.hour}:00</span>
                              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-[#007AFF]"
                                  style={{ width: `${Math.min((h.avg_engagement / Math.max(...engagement.best_posting_hours.map(x => x.avg_engagement), 1)) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-zinc-400 w-12 text-right">{h.avg_engagement} avg</span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Activity Heatmap */}
                    {engagement.activity_heatmap?.length > 0 && (
                      <Card className="bg-[#141414] border-white/10 rounded-xl p-5">
                        <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Activity Heatmap</h3>
                        <ActivityHeatmap data={engagement.activity_heatmap} />
                      </Card>
                    )}
                  </>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
